// Extract pasteable attachment files from a clipboard payload.
//
// A screenshot copied with Cmd/Ctrl+Shift+4, "Copy image" in a browser, or
// Snipping Tool arrives on the clipboard as a `DataTransferItem` of kind
// 'file' — never as a filename, so the <input type="file"> flow can't see it.
// Pasted images almost always have a useless name ('image.png' or ''), so we
// mint a stable, human-readable one per paste; UploadThing needs a real
// filename and the reporter needs to tell two pastes apart.
//
// Pure except for the `File` constructor: no DOM access, no upload calls, so
// this is unit-testable with a plain fake ClipboardEvent.

/** Minimal structural view of the clipboard bits we read. */
type ClipboardItemLike = {
  kind: string;
  type: string;
  getAsFile: () => File | null;
};

type ClipboardDataLike = {
  items?: ArrayLike<ClipboardItemLike> | null;
  files?: ArrayLike<File> | null;
};

const EXT_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/svg+xml': 'svg',
};

// A pasted file whose name is missing or a generic placeholder gets renamed.
const isPlaceholderName = (name: string): boolean =>
  !name || /^image\.[a-z0-9]+$/i.test(name) || !/\.[a-z0-9]+$/i.test(name);

/**
 * Build the display name for a pasted file. `stamp` is supplied by the caller
 * (never read from the clock here) so the result is deterministic in tests.
 */
export const pastedFileName = (mime: string, stamp: string): string => {
  const ext = EXT_BY_MIME[mime] ?? mime.split('/')[1] ?? 'png';
  return `pasted-${stamp}.${ext}`;
};

/**
 * Files carried by a paste, with placeholder-named ones renamed to
 * `pasted-<stamp>.<ext>`. Returns [] for a text-only paste, so the caller can
 * fall through to the browser's default text-insertion behaviour.
 *
 * Type/size limits are NOT applied here — the caller runs the same
 * `validateAttachmentFile` used by the file-picker path, so both entry points
 * enforce one rule set.
 */
export const filesFromClipboard = (
  clipboardData: ClipboardDataLike | null | undefined,
  stamp: string
): File[] => {
  if (!clipboardData) return [];

  // Prefer `items` (the only place a screenshot shows up in Chrome/Safari);
  // fall back to `files` for browsers that only populate that.
  const collected: File[] = [];
  const items = clipboardData.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item?.kind !== 'file') continue;
      const file = item.getAsFile();
      if (file) collected.push(file);
    }
  }
  if (collected.length === 0 && clipboardData.files?.length) {
    const files = clipboardData.files;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (file) collected.push(file);
    }
  }

  return collected.map((file, index) => {
    if (!isPlaceholderName(file.name)) return file;
    // Multiple images in one paste get a -2, -3 … suffix so names stay unique.
    const suffix = index === 0 ? stamp : `${stamp}-${index + 1}`;
    return new File([file], pastedFileName(file.type, suffix), {
      type: file.type,
      lastModified: file.lastModified,
    });
  });
};
